// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { addDays, format, isBefore } from "date-fns";
import React from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  type TimeSeriesDrilldown,
  useCampaignMetricDrilldown,
  useCampaignMetrics,
} from "@/hooks/api/use-campaign-metrics";
import { useCampaignSelection } from "@/hooks/use-campaign-selection";
import { useCampaignStatusSelection } from "@/hooks/use-campaign-status-selection";
import { formatDate, formatMonthYear } from "@/lib/date-formatting";
import type { MetricsFilters } from "@/lib/filters";

interface ProcessedData {
  data: Array<{
    rawDate: string;
    touchpoints: number;
    cumulative: number;
    days_since?: number;
  }>;
  firstEngagementDate: string | null;
  firstEngagementTimestamp: number | null;
  latestEngagementDate: string | null;
  latestEngagementTimestamp: number | null;
  firstTouchpoints: number;
  latestTouchpoints: number;
}

export function CampaignEngagementChart({
  campaignId,
  statuses,
  metric = "days_since_first_engagement",
  filters = {},
}: {
  campaignId?: string | null;
  statuses?: string[];
  metric?:
    | "days_since_first_engagement"
    | "days_since_latest_engagement"
    | "campaign_active_days";
  filters?: MetricsFilters;
}) {
  const { selectedCampaignId } = useCampaignSelection();
  const { selectedStatuses } = useCampaignStatusSelection(selectedCampaignId);
  const [showCumulative, setShowCumulative] = React.useState(false);

  // Use provided props or fall back to context values
  const effectiveCampaignId = campaignId || selectedCampaignId;
  const effectiveStatuses = statuses || selectedStatuses;

  const { data: timeSeriesData, isLoading } = useCampaignMetricDrilldown({
    campaignId: effectiveCampaignId || null,
    metric: metric,
    statuses: effectiveStatuses,
    filters,
    enabled: !!effectiveCampaignId,
  });

  const { data: metrics } = useCampaignMetrics({
    campaignId: effectiveCampaignId || null,
    statuses: effectiveStatuses,
    filters,
    enabled: !!effectiveCampaignId,
    metrics: [
      "days_since_first_engagement",
      "days_since_latest_engagement",
      "campaign_active_days",
    ],
  });

  // Process the raw data
  const processedData = React.useMemo<ProcessedData>(() => {
    if (!timeSeriesData?.length) {
      return {
        data: [],
        firstEngagementDate: null,
        firstEngagementTimestamp: null,
        latestEngagementDate: null,
        latestEngagementTimestamp: null,
        firstTouchpoints: 0,
        latestTouchpoints: 0,
      };
    }

    // Map the API data to our chart format
    const chartData = (timeSeriesData as TimeSeriesDrilldown[]).map((item) => ({
      rawDate: item.date,
      timestamp: new Date(item.date).getTime(),
      touchpoints: item.engagement_count,
    }));

    // Sort by date
    const sortedData = chartData.sort((a, b) => a.timestamp - b.timestamp);

    // Fill in missing dates with zero values
    const dateMap = new Map(sortedData.map((item) => [item.timestamp, item]));

    if (sortedData.length > 0) {
      const startDate = addDays(new Date(sortedData[0].rawDate), -5);
      const endDate =
        metric === "campaign_active_days"
          ? addDays(new Date(sortedData[sortedData.length - 1].rawDate), 7)
          : new Date();

      // Add entries for missing dates with zero touchpoints
      for (
        let currentDate = new Date(startDate);
        isBefore(currentDate, endDate);
        currentDate = addDays(currentDate, 1)
      ) {
        const timestamp = currentDate.getTime();
        if (!dateMap.has(timestamp)) {
          dateMap.set(timestamp, {
            rawDate: format(currentDate, "yyyy-MM-dd"),
            timestamp,
            touchpoints: 0,
          });
        }
      }
    }

    // Convert map back to array and sort chronologically
    const completeData = Array.from(dateMap.values()).sort(
      (a, b) => a.timestamp - b.timestamp,
    );

    // Find first and latest engagement dates
    const engagementDates = completeData.filter((d) => d.touchpoints > 0);

    let firstEngagementDate: string | null = null;
    let firstEngagementTimestamp: number | null = null;
    let latestEngagementDate: string | null = null;
    let latestEngagementTimestamp: number | null = null;
    let firstTouchpoints: number = 0;
    let latestTouchpoints: number = 0;

    if (engagementDates.length > 0) {
      const firstEntry = engagementDates[0];
      const lastEntry = engagementDates[engagementDates.length - 1];

      firstEngagementDate = firstEntry.rawDate;
      firstEngagementTimestamp = firstEntry.timestamp;
      firstTouchpoints = firstEntry.touchpoints;

      latestEngagementDate = lastEntry.rawDate;
      latestEngagementTimestamp = lastEntry.timestamp;
      latestTouchpoints = lastEntry.touchpoints;
    }

    // Calculate cumulative values
    const finalData = completeData.reduce<
      Array<{
        rawDate: string;
        timestamp: number;
        touchpoints: number;
        cumulative: number;
      }>
    >((acc, curr, index) => {
      const prevCumulative = index > 0 ? acc[index - 1].cumulative : 0;
      acc.push({
        ...curr,
        cumulative: prevCumulative + curr.touchpoints,
      });
      return acc;
    }, []);

    return {
      data: finalData,
      firstEngagementDate,
      firstEngagementTimestamp,
      latestEngagementDate,
      latestEngagementTimestamp,
      firstTouchpoints,
      latestTouchpoints,
    };
  }, [timeSeriesData, metric]);

  const {
    data,
    firstEngagementDate,
    firstEngagementTimestamp,
    latestEngagementDate,
    latestEngagementTimestamp,
    firstTouchpoints,
    latestTouchpoints,
  } = processedData;

  const calculateYAxisTicks = React.useMemo(() => {
    if (!data?.length) return [];

    // Calculate max value based on view mode
    const maxValue = showCumulative
      ? Math.max(...data.map((d) => d.cumulative))
      : Math.max(...data.map((d) => d.touchpoints));

    // Round up to next multiple of 5 for cleaner divisions
    const roundedMax = Math.ceil(maxValue / 5) * 5;
    return Array.from({ length: 6 }, (_, i) =>
      Math.min(maxValue, Math.round((i * roundedMax) / 5)),
    );
  }, [data, showCumulative]);

  return (
    <div>
      <div className="pb-4 flex items-center justify-between">
        <h3 className="text-base font-medium">Campaign engagement over time</h3>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">
              Show cumulative
            </span>
            <InfoTooltip
              trigger={
                <button
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <InfoCircledIcon className="h-3 w-3" />
                </button>
              }
              side="top"
              className="w-[250px]"
            >
              <div className="text-sm">
                <h4 className="font-medium mb-1">Cumulative View</h4>
                <p className="text-muted-foreground">
                  Shows the total cumulative engagements over time, helping you
                  understand the overall growth trend of campaign engagement.
                </p>
              </div>
            </InfoTooltip>
          </div>
          <Switch
            checked={showCumulative}
            onCheckedChange={setShowCumulative}
            aria-label="Toggle cumulative view"
            className="scale-75 data-[state=checked]:bg-primary/80"
          />
        </div>
      </div>
      {isLoading ? (
        <div className="flex h-[300px] w-full items-center justify-center">
          <Skeleton className="h-[300px] w-full" />
        </div>
      ) : !data?.length ? (
        <div className="flex h-[300px] w-full items-center justify-center">
          <p className="text-sm text-muted-foreground">No data available</p>
        </div>
      ) : (
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{ top: 30, right: 30, left: 0, bottom: 10 }}
            >
              <CartesianGrid
                vertical={false}
                stroke="hsl(var(--muted))"
                opacity={0}
              />
              <XAxis
                dataKey="timestamp"
                tickFormatter={formatMonthYear}
                tick={{ fontSize: 12 }}
                minTickGap={30}
                type="category"
                domain={["auto", "auto"]}
                stroke="hsl(var(--muted-foreground))"
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 12 }}
                stroke="hsl(var(--muted-foreground))"
                domain={[
                  0,
                  () => {
                    // Find the actual maximum value based on the view mode
                    const actualMax = showCumulative
                      ? Math.max(...data.map((d) => d.cumulative))
                      : Math.max(...data.map((d) => d.touchpoints));

                    // Add padding for specific metrics
                    return metric === "days_since_first_engagement" ||
                      metric === "days_since_latest_engagement" ||
                      metric === "campaign_active_days"
                      ? Math.ceil(actualMax * 1.2)
                      : actualMax;
                  },
                ]}
                ticks={calculateYAxisTicks}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid var(--border)",
                  borderRadius: "0.5rem",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  fontSize: "0.75rem",
                  padding: "0.5rem",
                  color: "hsl(240 10% 3.9%)",
                }}
                formatter={(value: number, name: string) => {
                  if (name === "touchpoints")
                    return [value, "Daily Engagements"];
                  if (name === "cumulative")
                    return [value, "Total Engagements"];
                  return [value, name];
                }}
                labelFormatter={(rawDate) => {
                  // Convert timestamp to local date string in YYYY-MM-DD format
                  const date = new Date(rawDate);
                  return formatDate(date.toISOString().split("T")[0]);
                }}
              />
              {firstEngagementDate && firstEngagementTimestamp && (
                <ReferenceLine
                  x={firstEngagementTimestamp}
                  stroke="hsl(var(--ring))"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  yAxisId="left"
                  isFront={false}
                  segment={[
                    { y: Math.max(0, firstTouchpoints - 5) },
                    { y: firstTouchpoints + 10 },
                  ]}
                  label={({ viewBox }) => {
                    return (
                      <g>
                        <rect
                          x={viewBox.x - 50 + 35}
                          y={viewBox.y - 20}
                          width="100"
                          height="40"
                          fill="hsl(var(--background))"
                          rx="4"
                          className="drop-shadow-sm"
                        />
                        <text
                          x={viewBox.x + 35}
                          y={viewBox.y}
                          textAnchor="middle"
                          fill="hsl(var(--foreground))"
                          fontSize="12"
                        >
                          {firstEngagementDate
                            ? formatDate(firstEngagementDate)
                            : ""}
                        </text>
                        <text
                          x={viewBox.x + 35}
                          y={viewBox.y + 16}
                          textAnchor="middle"
                          fill="hsl(var(--muted-foreground))"
                          fontSize="11"
                        >
                          {metrics?.days_since_first_engagement} days ago
                        </text>
                      </g>
                    );
                  }}
                />
              )}
              {latestEngagementDate && latestEngagementTimestamp && (
                <ReferenceLine
                  x={latestEngagementTimestamp}
                  stroke="hsl(var(--ring))"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  yAxisId="left"
                  isFront={false}
                  segment={[
                    { y: Math.max(0, latestTouchpoints - 5) },
                    { y: latestTouchpoints + 10 },
                  ]}
                  label={({ viewBox }) => {
                    return (
                      <g>
                        <rect
                          x={viewBox.x - 50 - 35}
                          y={viewBox.y - 20}
                          width="100"
                          height="40"
                          fill="hsl(var(--background))"
                          rx="4"
                          className="drop-shadow-sm"
                        />
                        <text
                          x={viewBox.x - 35}
                          y={viewBox.y}
                          textAnchor="middle"
                          fill="hsl(var(--foreground))"
                          fontSize="12"
                        >
                          {latestEngagementDate
                            ? formatDate(latestEngagementDate)
                            : ""}
                        </text>
                        <text
                          x={viewBox.x - 35}
                          y={viewBox.y + 16}
                          textAnchor="middle"
                          fill="hsl(var(--muted-foreground))"
                          fontSize="11"
                        >
                          {metrics?.days_since_latest_engagement} days ago
                        </text>
                      </g>
                    );
                  }}
                />
              )}
              <Bar
                dataKey="touchpoints"
                yAxisId="left"
                opacity={0.8}
                fill={
                  !showCumulative
                    ? "hsl(var(--primary))"
                    : "hsl(var(--muted-foreground))"
                }
              />
              {showCumulative && (
                <Line
                  type="monotone"
                  dataKey="cumulative"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  yAxisId="left"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
